/**
 * IT training, "Priority, SLA and escalation" (16 Oct): the incident
 * reference the lesson draws, in ONE place. The lesson file only places the
 * pictures (`{{sla-matrix}}`, `{{sla-targets}}`, `{{sla-escalation}}`); every
 * number on them comes from here, so changing a target is changing this file.
 *
 * SOURCE: Rusty's slide "Priority, SLA and escalation — Incidents" (16 Oct).
 *
 * NOT YET ALIGNED WITH LIVE. A read-only read of LIVE's escalation schedules
 * (itsm-ro `sla-schedules`, 14 Oct) holds different incident targets:
 *
 *     P1 0.5 h / 24 h   P2 0.5 h / 24 h   P3 48 h / 72 h
 *     P4 24 h / 120 h   P5 48 h / 240 h          (response / resolution)
 *
 * Which side changes is Rusty's call; until then the lesson shows the slide
 * and carries {@link SLA_ALIGNMENT_NOTE} as its admin note.
 *
 * Kept import-free so `node --test` loads it from the source.
 */

export const SLA_ALIGNMENT_NOTE = "Targets to be aligned with the configured LIVE schedules.";

export type SlaLevel = "high" | "medium" | "low";
export const SLA_LEVELS: readonly SlaLevel[] = ["high", "medium", "low"];

export type SlaPriority = 1 | 2 | 3 | 4 | 5;
export const SLA_PRIORITIES: readonly SlaPriority[] = [1, 2, 3, 4, 5];

/** Rows = Impact, columns = Urgency, both High → Low. */
export const SLA_MATRIX: Readonly<Record<SlaLevel, Readonly<Record<SlaLevel, SlaPriority>>>> = {
  high: { high: 1, medium: 2, low: 3 },
  medium: { high: 2, medium: 3, low: 4 },
  low: { high: 3, medium: 4, low: 5 },
};

export type SlaUnit = "minute" | "hour" | "day";

export interface SlaDuration {
  value: number;
  unit: SlaUnit;
}

export interface SlaTarget {
  response: SlaDuration;
  resolution: SlaDuration;
}

/** Response and resolution per priority, on the business calendar. */
export const SLA_TARGETS: Readonly<Record<SlaPriority, SlaTarget>> = {
  1: { response: { value: 30, unit: "minute" }, resolution: { value: 1, unit: "day" } },
  2: { response: { value: 2, unit: "hour" }, resolution: { value: 3, unit: "day" } },
  3: { response: { value: 1, unit: "day" }, resolution: { value: 5, unit: "day" } },
  4: { response: { value: 2, unit: "day" }, resolution: { value: 10, unit: "day" } },
  5: { response: { value: 3, unit: "day" }, resolution: { value: 15, unit: "day" } },
};

export type SlaRecipient = "owner" | "teamManager" | "ownerTeam";

export interface SlaEscalationStep {
  /** Per cent of the SLA time gone. */
  at: 25 | 50 | 75 | 100;
  /** Who hears about the Response clock; empty = no notification. */
  response: readonly SlaRecipient[];
  /** Who hears about the Resolution clock; empty = no notification. */
  resolution: readonly SlaRecipient[];
  /** A reminder rather than the first notice (the slide's 50%). */
  reminder?: boolean;
}

export const SLA_ESCALATION: readonly SlaEscalationStep[] = [
  { at: 25, response: [], resolution: ["owner"] },
  { at: 50, response: [], resolution: ["owner"], reminder: true },
  {
    at: 75,
    response: ["owner", "teamManager", "ownerTeam"],
    resolution: ["owner", "teamManager", "ownerTeam"],
  },
  { at: 100, response: ["owner"], resolution: ["owner", "ownerTeam"] },
];

/** The one shared hours-of-operation calendar every clock runs on. */
export const SLA_CALENDAR = { hoursPerDay: 22, daysPerWeek: 5 } as const;

/** Impact × Urgency → Priority. */
export function priorityFor(impact: SlaLevel, urgency: SlaLevel): SlaPriority {
  return SLA_MATRIX[impact][urgency];
}
